import React, { createContext, useCallback, useContext, useState, useEffect, ReactNode } from 'react';

interface LanguageContextType {
  language: string;
  setLanguage: (lang: string) => void;
  /** Applies the admin's default only while the visitor has made no choice. */
  applyDefaultLanguage: (lang: string) => void;
  isRTL: boolean;
  t: (key: string, fallback?: string) => string;
}

interface LanguageProviderProps {
  children: ReactNode;
}

// Translation dictionary
const translations: { [key: string]: { [key: string]: string } } = {
  en: {
    // Navigation
    dashboard: 'Dashboard',
    adminDashboard: 'Admin Dashboard',
    editorDashboard: 'Editor Dashboard',
    clientDashboard: 'Client Dashboard',
    welcomeBack: 'Welcome back',
    exportReport: 'Export Report',
    totalApplications: 'Total Applications',
    pendingApprovalStat: 'Pending Approval',
    activeUsers: 'Active Users',
    manageAssignedApps: 'Manage your assigned applications',
    assignedTasksStat: 'Assigned Tasks',
    inProgressStat: 'In Progress',
    completedTodayStat: 'Completed Today',
    overdueStat: 'Overdue',
    applications: 'Applications',
    allTransactions: 'All Applications',
    createTransaction: 'Create Application',
    userManagement: 'User Management',
    notifications: 'Notifications',
    reportsAnalytics: 'Reports & Analytics',
    assignments: 'Assignments',
    emailTemplates: 'Email Templates',
    auditLogs: 'Audit Logs',
    systemSettings: 'System Settings',
    myTransactions: 'My Applications',
    createNew: 'Create New',
    assignedTransactions: 'Assigned Applications',
    bulkImport: 'Bulk Import',
    drafts: 'Drafts',
    reports: 'Reports',
    documents: 'Documents',
    projects: 'Projects',
    allProjects: 'All Projects',
    myProjects: 'My Projects',
    createProject: 'Create Project',
    searchTransactions: 'Search applications...',
    profile: 'Profile',
    settings: 'Settings',
    logout: 'Logout',

    // Dashboard
    myDashboard: 'My Dashboard',
    trackApplicationsStatus: 'Track your applications and status',
    activeApplications: 'Active Applications',
    completed: 'Completed',
    awaitingResponse: 'Awaiting Response',
    recentApplications: 'Recent Applications',
    viewAll: 'View All',

    // Table Headers
    referenceNumber: 'Reference Number',
    applicationId: 'Application ID',
    applicationTitle: 'Application Title',
    client: 'User',
    company: 'Company/Owner',
    type: 'Type',
    status: 'Status',
    assignedTo: 'Assigned To',
    lastModified: 'Last Modified',
    attachments: 'Attachments',
    comments: 'Comments',
    actions: 'Actions',
    noApplicationsFound: 'No applications found',

    // Transactions Page
    allApplications: 'All Applications',
    manageAllApplications: 'Manage and track all applications in the system',
    newApplication: 'New Application',
    advancedFilter: 'Advanced Filter',
    showing: 'Showing',
    to: 'to',
    of: 'of',
    entries: 'entries',
    refresh: 'Refresh',
    view: 'View',
    edit: 'Edit',
    duplicate: 'Duplicate',
    delete: 'Delete',
    previous: 'Previous',
    next: 'Next',
    saveAsDraft: 'Save as Draft',
    submitTransaction: 'Submit Transaction',
    submitting: 'Submitting...',

    // Notifications
    markAllAsRead: 'Mark all as read',
    clearAll: 'Clear all',
    viewAllNotifications: 'View All Notifications',
    noNotifications: 'No notifications',
    manageViewNotifications: 'Manage and view all your notifications',
    all: 'All',
    unread: 'Unread',
    system: 'System',
    users: 'Users',

    // Profile/Settings
    profileSettings: 'Profile Settings',
    manageProfileSettings: 'Manage your profile and account settings',
    profileInformation: 'Profile Information',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email',
    phoneNumber: 'Phone Number',
    companyName: 'Company Name',
    languagePreference: 'Language Preference',
    emailCannotBeChanged: 'Email cannot be changed',
    saveProfile: 'Save Profile',
    saving: 'Saving...',
    security: 'Security',
    keepAccountSecure: 'Keep your account secure by regularly updating your password',
    changePassword: 'Change Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    nationalId: 'National ID',
    nationalIdOptionalHint: 'Optional — used on official documents',
    alreadyHaveAccount: 'Already have an account?',
    fillDetailsToRegister: 'Fill in your details to register',
    registrationFailed: 'Registration failed. Please try again.',
    registrationSuccessTitle: 'Registration Successful!',
    registrationPendingApproval: 'Thank you for registering. Your account has been created and is pending admin approval.',
    registrationCheckEmail: 'You will receive an email once your account has been approved. Please check your email for verification instructions.',
    returnToLogin: 'Return to Login',
    registeringWith: "You're registering with",
    adminWillApprove: 'An admin from this organization will approve your access.',
    createAccountWelcome: 'Create an account to start tracking your applications',
    creatingAccount: 'Creating Account...',
    main: 'Main',
    administration: 'Administration',
    searchMenu: 'Search menu…',
    forgotPasswordTitle: 'Forgot Password?',
    forgotPasswordSubtitle: 'Enter your email and we will send you a reset link',
    sendResetLink: 'Send Reset Link',
    backToLogin: 'Back to Login',
    resetLinkSent: 'Reset Link Sent',
    checkYourEmail: 'Check your email for the password reset link',
    resetPasswordTitle: 'Reset Password',
    resetPasswordSubtitle: 'Enter your new password below',
    confirmPassword: 'Confirm Password',
    invalidToken: 'Invalid or expired reset link',
    passwordTooShort: 'Password must be at least 8 characters long',
    passwordsDoNotMatch: 'Passwords do not match',
    passwordResetFailed: 'Failed to reset password. Please try again.',
    passwordResetSuccess: 'Password Reset Successfully',
    resetPassword: 'Reset Password',
    confirmNewPassword: 'Confirm New Password',
    passwordMinLength: 'Minimum 8 characters',
    cancel: 'Cancel',
    changing: 'Changing...',
    clearForm: 'Clear Form',
    resetChanges: 'Reset Changes',
    clearFormConfirm: 'Are you sure you want to clear all entered data? This action cannot be undone.',
    resetFormConfirm: 'Are you sure you want to reset all changes? This will revert all fields to their original values.',
    searchAssignee: 'Search assignee...',
    searchClient: 'Search client...',
    noUsersFound: 'No users found',
    noClientsFound: 'No clients found',

    // Time
    justNow: 'Just now',
    minutesAgo: 'minutes ago',
    hoursAgo: 'hours ago',
    daysAgo: 'days ago',

    // Filters
    filter: 'Filter',
    filters: 'Filters',
    advancedFilters: 'Advanced Filters',
    clearFilters: 'Clear Filters',
    applyFilters: 'Apply Filters',
    allStatus: 'All Status',
    allPriorities: 'All Priorities',
    allTypes: 'All Types',
    allAssignees: 'All Assignees',
    allDepartments: 'All Departments',
    allClients: 'All Clients',
    allUsers: 'All Users',
    unassigned: 'Unassigned',
    fromDate: 'From Date',
    toDate: 'To Date',
    dateFilters: 'Date Filters',
    createdFrom: 'Created From',
    createdTo: 'Created To',
    dueDateFrom: 'Due Date From',
    dueDateTo: 'Due Date To',
    tags: 'Tags',
    enterTagsComma: 'Enter tags separated by commas',
    withAttachments: 'With Attachments',
    withoutAttachments: 'Without Attachments',
    reset: 'Reset',
    export: 'Export',
    exportToExcel: 'Export to Excel',
    exportToCSV: 'Export to CSV',
    createdBy: 'Created By',
    department: 'Department',
    dueDate: 'Due Date',
    priority: 'Priority',

    // Transaction Creation Form
    basicApplicationInformation: 'Basic Application Information',
    projectId: 'Project ID',
    enterProjectId: 'Enter project ID (optional)',
    enterApplicationTitle: 'Enter application title',
    externalReferenceId: 'External Reference ID',
    exampleReference: 'e.g., REF-2024-XXXX',
    description: 'Description',
    enterDescription: 'Enter detailed description of the application',
    applicationType: 'Application Type',
    selectType: 'Select type',
    selectPriority: 'Select priority',
    selectStatus: 'Select status',
    clientEmail: 'Client Email',
    autoFilledClient: 'Will be auto-filled when client is selected',
    autoFilledFromClient: 'Auto-filled from client data',
    clientPhone: 'Client Phone',

    // Transaction Summary/Review
    reviewTransactionDetails: 'Review Transaction Details',
    applicationSummary: 'Application Summary',

    // Login Page
    pleaseSignIn: 'Please Sign In',
    emailAddress: 'Email Address',
    enterYourEmail: 'Enter your email',
    password: 'Password',
    enterYourPassword: 'Enter your password',
    rememberMe: 'Remember Me',
    forgotPassword: 'Forgot Password?',
    signIn: 'Sign In',
    dontHaveAccount: "Don't have an account?",
    createAccount: 'Create Account',
    secureAccess: 'Secure Access',
    realTimeTracking: 'Real-Time Tracking',
    teamCollaboration: 'Team Collaboration',
    // Login page (design v2)
    loginTitle: 'Sign in',
    loginSubtitle: 'Enter your details to continue',
    loginEmail: 'Email',
    loginForgot: 'Forgot password?',
    loginSigningIn: 'Signing in…',
    loginShowPassword: 'Show password',
    loginHidePassword: 'Hide password',
    loginEmailInvalid: 'Enter a valid email address',
    loginPasswordRequired: 'Enter your password',
    loginSystemName: 'Transaction Tracking System',

    // Permission Errors
    permission_view_denied: "You don't have permission to view this application",
    permission_edit_denied: "You don't have permission to edit this application",
    permission_comment_denied: "You don't have permission to comment on this application",
    permission_access_denied: "You don't have permission to access this application",
    permission_duplicate_denied: "You don't have permission to duplicate applications",
    failed_to_load: 'Failed to load data',

    // Documents
    myDocuments: 'My Documents',
    createDocument: 'Create Document',
    documentId: 'Document ID',
    title: 'Title',
    templateName: 'Template Name',
    mode: 'Mode',
    completionPercentage: 'Completion %',
    noDocumentsFound: 'No documents found',
    searchDocuments: 'Search documents...',
    allModes: 'All Modes',
    loading: 'Loading...',

    // Document Create
    selectTemplate: 'Select Template',
    documentSetup: 'Document Setup',
    contentEditor: 'Content Editor',
    back: 'Back',
    templateCategory: 'Category',
    sections: 'Sections',
    searchTemplates: 'Search templates...',
    allCategories: 'All Categories',
    selectTemplateFirst: 'Please select a template to continue',
    titleEn: 'Title (English)',
    titleAr: 'Title (Arabic)',
    clientProfile: 'Client Profile',
    project: 'Project',
    transaction: 'Transaction',
    creationMode: 'Creation Mode',
    manual: 'Manual',
    aiAssisted: 'AI Assisted',
    manualDescription: 'Write content manually',
    aiDescription: 'Generate content with AI assistance',
    selectClient: 'Select client...',
    selectProject: 'Select project...',
    selectTransaction: 'Select transaction...',

    // Content Editor
    progress: 'Progress',
    sectionTitle: 'Section Title',
    generateWithAI: 'Generate with AI',
    generating: 'Generating...',
    accept: 'Accept',
    regenerate: 'Regenerate',
    markAsComplete: 'Mark as Complete',
    saveDraft: 'Save Draft',
    completeDocument: 'Complete Document',
    enterPrompt: 'Enter prompt for AI generation...',
    generatedContent: 'Generated Content',
    autoSaving: 'Auto-saving...',

    // Landing Page v3 (Greptile style)
    nav_product: 'Product', nav_features: 'Features', nav_pricing: 'Pricing', nav_login: 'Sign In', nav_start: 'Get Started',
    hero_badge: 'AI-POWERED PLATFORM', hero_title_line1: 'Run your business', hero_title_line2: 'smarter',
    hero_tagline: 'ONE PLATFORM TO MANAGE YOUR PROJECTS, TRANSACTIONS,\nMEETINGS, AND DOCUMENTS — POWERED BY AI',
    reasons_title: 'Why Nuagent',
    reasons_why: 'Why',
    reason_1: 'Create technical & financial proposals for your clients in 3 minutes',
    reason_2: 'Draft meeting minutes and track approvals effortlessly',
    reason_3: 'Use smart templates for contracts and official correspondence',
    reason_4: 'Monitor all your projects and transactions from one dashboard',
    hero_cta_start: 'Start For Free', hero_cta_sub: 'no credit card required • 14-day free trial', hero_mockup: '[ Product Demo ]',
    trust_label: 'TRUSTED BY 500+ BUSINESSES TO RUN THEIR DAILY OPERATIONS', trust_link: 'SEE WHY TEAMS LOVE NUAGENT',
    trust_secure: 'Private & Secure', trust_anywhere: 'Access Anywhere', trust_support: '24/7 Support',
    trust_uptime: 'Always Online', trust_mobile: 'Mobile Ready', trust_bilingual: 'Bilingual AR/EN',
    feat_badge: 'CORE MODULES', feat_title: 'Your second pair of hands',
    feat_transactions_badge: 'TRANSACTIONS', feat_transactions_title: 'Track every application from start to finish',
    feat_transactions_desc: 'Auto-generated IDs, QR codes, smart assignment, bulk operations, and real-time status updates.',
    feat_transactions_visual: '[ Transaction Tracker ]',
    feat_projects_badge: 'PROJECTS', feat_projects_title: 'Projects that actually move forward',
    feat_projects_desc: 'Milestones, activities, progress tracking, team collaboration. Visual dashboards keep everyone aligned.',
    feat_projects_visual: '[ Project Dashboard ]',
    feat_proposals_badge: 'PROPOSALS', feat_proposals_title: 'Professional proposals in minutes',
    feat_proposals_desc: 'AI-powered templates, drag-and-drop sections, payment schedules, and one-click PDF generation.',
    feat_proposals_visual: '[ Proposal Builder ]',
    feat_meetings_badge: 'MEETINGS', feat_meetings_title: 'Meeting minutes that write themselves',
    feat_meetings_desc: 'Track attendees, action items, decisions, and approvals with multi-level digital workflows.',
    feat_meetings_visual: '[ MOM Tracker ]',
    ai_badge: 'AI-POWERED', ai_title: 'Intelligence built into every workflow.',
    ai_proposal_title: 'AI Proposal Builder', ai_proposal_desc: 'Upload any design template and our AI vision engine extracts the layout, maps content zones, and generates pixel-perfect proposals automatically.',
    ai_docs_title: 'Smart Document Generation', ai_docs_desc: 'Generate professional documents, reports, and certificates using AI. Bilingual content, auto-filled data, and brand-consistent formatting.',
    ai_assign_title: 'Intelligent Auto-Assignment', ai_assign_desc: 'AI-driven task assignment, auto-approval rules, deadline escalations, and workload balancing across your team.',
    security_badge: 'SECURITY', security_title: 'Security-First Design',
    security_onprem_title: 'On-Premise Deployment', security_onprem_desc: 'Deploy in your own environment. Your data never leaves your building.',
    security_enterprise_title: 'Enterprise Grade Security', security_enterprise_desc: 'Role-based access, audit trails, encrypted data, and GDPR-ready compliance.',
    testimonials_badge: 'TESTIMONIALS', testimonials_title: 'From Businesses That Use Nuagent',
    testimonials_title_prefix: 'From Businesses That Use',
    testimonial_1_quote: 'Nuagent transformed how we track our construction permits. What used to take days now takes hours.',
    testimonial_1_name: 'Eng. Mohammed Al-Rashid', testimonial_1_role: 'Project Director, Al-Rashid Construction',
    testimonial_2_quote: 'The bilingual support is a game-changer. Our team works in Arabic, our clients get reports in English.',
    testimonial_2_name: 'Sara Al-Zahrani', testimonial_2_role: 'Operations Manager, Gulf Consulting',
    testimonial_3_quote: 'Finally a platform that understands Saudi business. The MOM module alone saved us 10 hours per week.',
    testimonial_3_name: 'Fahad Al-Otaibi', testimonial_3_role: 'CEO, Nuwas Technologies',
    pricing_badge: 'PRICING', pricing_title: 'Simple pricing for any business.', pricing_subtitle: 'Choose the plan that fits your team. All plans include bilingual support.',
    plan_starter_name: 'Basic', plan_starter_users: '', plan_starter_price: 'SAR 25', plan_starter_period: '/user/month',
    plan_starter_desc: 'Perfect for small teams', plan_starter_cta: 'Choose Package',
    plan_starter_f1: 'All core modules', plan_starter_f2: '', plan_starter_f3: '10 GB storage',
    plan_starter_f4: 'Email support', plan_starter_f5: 'Cloud hosting', plan_starter_f6: 'Bilingual interface',
    plan_business_name: 'Professional', plan_business_users: '', plan_business_popular: 'Most Popular',
    plan_business_price: 'SAR 75', plan_business_period: '/user/month', plan_business_desc: 'Ideal for growing companies',
    plan_business_cta: 'Choose Package',
    plan_business_f1: 'Everything in Basic', plan_business_f2: '', plan_business_f3: '100 GB storage',
    plan_business_f4: 'Priority support', plan_business_f5: 'AI proposal builder', plan_business_f6: 'Advanced analytics',
    plan_business_f7: 'Custom branding', plan_business_f8: 'API access',
    plan_enterprise_name: 'Enterprises & Companies', plan_enterprise_users: '', plan_enterprise_price: 'Contact Us',
    plan_enterprise_desc: 'Tailored for large organizations', plan_enterprise_cta: 'Contact Sales',
    plan_enterprise_f1: 'Everything in Professional', plan_enterprise_f2: 'Unlimited users', plan_enterprise_f3: 'Unlimited storage',
    plan_enterprise_f4: 'On-premise option', plan_enterprise_f5: 'Dedicated manager', plan_enterprise_f6: 'Custom integrations',
    plan_enterprise_f7: 'SLA guarantee', plan_enterprise_f8: 'Training & onboarding',
    faq_badge: 'FAQ', faq_title: 'Frequently Asked Questions',
    faq_q1: 'What is included in the free trial?', faq_a1: '14-day full access to all Business plan features. No credit card required. Cancel anytime.',
    faq_q2: 'Can I switch between Cloud and On-Premise?', faq_a2: 'Yes, we support seamless migration between cloud and on-premise deployments at any time.',
    faq_q3: 'Do you support Arabic and English?', faq_a3: 'Yes, full native bilingual support with RTL layout. Not a translation layer — built from the ground up.',
    faq_q4: 'Is my data secure?', faq_a4: '256-bit encryption, role-based access, comprehensive audit trails, and GDPR-ready compliance.',
    faq_q5: 'What sectors do you serve?', faq_a5: 'Construction, legal, finance, engineering, consulting, real estate, government, healthcare, and education.',
    faq_q6: 'How do I get started?', faq_a6: 'Sign up for free at nuagent.net. No credit card required. Your workspace is ready in under 2 minutes.',
    faq_contact: 'Still have questions?', faq_more: 'Contact Us',
    cta_title: 'Ready to simplify your operations?', cta_subtitle: 'Start your free 14-day trial today.',
    cta_button: 'Start For Free', cta_no_card: 'no credit card required',
    footer_tagline: 'Making business management simple, smart, and secure.',
    footer_product: 'Product', footer_company: 'Company', footer_legal: 'Legal',
    footer_contact: 'Contact', footer_about: 'About', footer_careers: 'Careers', footer_blog: 'Blog',
    footer_privacy: 'Privacy Policy', footer_terms: 'Terms of Service', footer_sla: 'SLA',
    footer_security: 'Security', footer_api_docs: 'API Docs',
    footer_rights: 'All rights reserved.', footer_powered: 'Powered by Nuwas',
    // Contact Page
    contact_hero_title: 'CONTACT SALES.', contact_hero_subtitle: 'Thanks for your interest in Nuagent. Fill out the form and our team will reach out ASAP.',
    contact_hero_subtitle_prefix: 'Thanks for your interest in',
    contact_hero_subtitle_suffix: '. Fill out the form and our team will reach out ASAP.',
    contact_first_name: 'FIRST NAME', contact_last_name: 'LAST NAME',
    contact_first_name_ph: 'John', contact_last_name_ph: 'Doe',
    contact_email: 'WORK EMAIL', contact_company: 'COMPANY NAME',
    contact_email_ph: 'john@company.com', contact_company_ph: 'Acme Inc.',
    contact_team_size: 'HOW MANY EMPLOYEES?', contact_team_select: 'Select team size',
    contact_message: 'WHAT WOULD BE HELPFUL FOR US TO KNOW?',
    contact_message_ph: 'Tell us about your business and what you are looking to achieve...',
    contact_submit: 'Contact Us',
    contact_success_title: 'Message Sent!', contact_success_desc: 'Thank you for reaching out. Our team will get back to you within 24 hours.',
  },
  ar: {
    // Navigation
    dashboard: 'لوحة التحكم',
    adminDashboard: 'لوحة تحكم المسؤول',
    editorDashboard: 'لوحة تحكم المحرر',
    clientDashboard: 'لوحة تحكم العميل',
    welcomeBack: 'مرحباً بعودتك',
    exportReport: 'تصدير التقرير',
    totalApplications: 'إجمالي المعاملات',
    pendingApprovalStat: 'بانتظار الموافقة',
    activeUsers: 'المستخدمون النشطون',
    manageAssignedApps: 'إدارة المعاملات المسندة إليك',
    assignedTasksStat: 'المهام المسندة',
    inProgressStat: 'قيد التنفيذ',
    completedTodayStat: 'أُكملت اليوم',
    overdueStat: 'متأخرة',
    applications: 'المعاملات',
    allTransactions: 'جميع المعاملات',
    createTransaction: 'إنشاء معاملة',
    userManagement: 'إدارة المستخدمين',
    notifications: 'الإشعارات',
    reportsAnalytics: 'التقارير والتحليلات',
    assignments: 'التكليفات',
    emailTemplates: 'قوالب البريد الإلكتروني',
    auditLogs: 'سجلات التدقيق',
    systemSettings: 'إعدادات النظام',
    myTransactions: 'معاملاتي',
    createNew: 'إنشاء جديد',
    assignedTransactions: 'المعاملات المكلفة',
    bulkImport: 'استيراد جماعي',
    drafts: 'المسودات',
    reports: 'التقارير',
    documents: 'المستندات',
    projects: 'المشاريع',
    allProjects: 'جميع المشاريع',
    myProjects: 'مشاريعي',
    createProject: 'إنشاء مشروع',
    searchTransactions: 'البحث عن المعاملات...',
    profile: 'الملف الشخصي',
    settings: 'الإعدادات',
    logout: 'تسجيل الخروج',

    // Dashboard
    myDashboard: 'لوحة التحكم',
    trackApplicationsStatus: 'تتبع معاملاتك وحالتها',
    activeApplications: 'المعاملات النشطة',
    completed: 'مكتمل',
    awaitingResponse: 'في انتظار الرد',
    recentApplications: 'المعاملات الأخيرة',
    viewAll: 'عرض الكل',

    // Table Headers
    referenceNumber: 'الرقم المرجعي',
    applicationId: 'رقم المعاملة',
    applicationTitle: 'عنوان المعاملة',
    client: 'المستخدم',
    company: 'المالك/الشركة',
    type: 'النوع',
    status: 'الحالة',
    assignedTo: 'مكلف إلى',
    lastModified: 'آخر تعديل',
    attachments: 'المرفقات',
    comments: 'التعليقات',
    actions: 'الإجراءات',
    noApplicationsFound: 'لا توجد معاملات',

    // Transactions Page
    allApplications: 'جميع المعاملات',
    manageAllApplications: 'إدارة وتتبع جميع المعاملات في النظام',
    newApplication: 'معاملة جديدة',
    advancedFilter: 'فلتر متقدم',
    showing: 'عرض',
    to: 'إلى',
    of: 'من',
    entries: 'إدخالات',
    refresh: 'تحديث',
    view: 'عرض',
    edit: 'تعديل',
    duplicate: 'نسخ',
    delete: 'حذف',
    previous: 'السابق',
    next: 'التالي',
    saveAsDraft: 'حفظ كمسودة',
    submitTransaction: 'إرسال المعاملة',
    submitting: 'جاري الإرسال...',

    // Notifications
    markAllAsRead: 'وضع علامة مقروءة على الكل',
    clearAll: 'مسح الكل',
    viewAllNotifications: 'عرض جميع الإشعارات',
    noNotifications: 'لا توجد إشعارات',
    manageViewNotifications: 'إدارة وعرض جميع إشعاراتك',
    all: 'الكل',
    unread: 'غير مقروءة',
    system: 'النظام',
    users: 'المستخدمون',

    // Profile/Settings
    profileSettings: 'إعدادات الملف الشخصي',
    manageProfileSettings: 'إدارة ملفك الشخصي وإعدادات الحساب',
    profileInformation: 'معلومات الملف الشخصي',
    firstName: 'الاسم الأول',
    lastName: 'اسم العائلة',
    email: 'البريد الإلكتروني',
    phoneNumber: 'رقم الهاتف',
    companyName: 'اسم الشركة',
    languagePreference: 'تفضيل اللغة',
    emailCannotBeChanged: 'لا يمكن تغيير البريد الإلكتروني',
    saveProfile: 'حفظ الملف الشخصي',
    saving: 'جاري الحفظ...',
    security: 'الأمان',
    keepAccountSecure: 'حافظ على أمان حسابك من خلال تحديث كلمة المرور بانتظام',
    changePassword: 'تغيير كلمة المرور',
    currentPassword: 'كلمة المرور الحالية',
    newPassword: 'كلمة المرور الجديدة',
    nationalId: 'رقم الهوية الوطنية',
    nationalIdOptionalHint: 'اختياري — يُستخدم في المستندات الرسمية',
    alreadyHaveAccount: 'لديك حساب بالفعل؟',
    fillDetailsToRegister: 'أدخل بياناتك لإنشاء حساب',
    registrationFailed: 'فشل التسجيل. يرجى المحاولة مرة أخرى.',
    registrationSuccessTitle: 'تم التسجيل بنجاح!',
    registrationPendingApproval: 'شكراً لتسجيلك. تم إنشاء حسابك وهو في انتظار موافقة المسؤول.',
    registrationCheckEmail: 'ستصلك رسالة بريد إلكتروني عند الموافقة على حسابك. يرجى التحقق من بريدك لتعليمات التفعيل.',
    returnToLogin: 'العودة لتسجيل الدخول',
    registeringWith: 'أنت تسجل لدى',
    adminWillApprove: 'سيقوم مسؤول من هذه المنظمة بالموافقة على وصولك.',
    createAccountWelcome: 'أنشئ حساباً لبدء تتبع طلباتك',
    creatingAccount: 'جاري إنشاء الحساب...',
    main: 'الرئيسية',
    administration: 'الإدارة',
    searchMenu: 'ابحث في القائمة…',
    forgotPasswordTitle: 'نسيت كلمة المرور؟',
    forgotPasswordSubtitle: 'أدخل بريدك الإلكتروني وسنرسل لك رابط إعادة التعيين',
    sendResetLink: 'إرسال رابط إعادة التعيين',
    backToLogin: 'العودة لتسجيل الدخول',
    resetLinkSent: 'تم إرسال رابط إعادة التعيين',
    checkYourEmail: 'تحقق من بريدك الإلكتروني للحصول على رابط إعادة تعيين كلمة المرور',
    resetPasswordTitle: 'إعادة تعيين كلمة المرور',
    resetPasswordSubtitle: 'أدخل كلمة المرور الجديدة أدناه',
    confirmPassword: 'تأكيد كلمة المرور',
    invalidToken: 'رابط إعادة التعيين غير صالح أو منتهي الصلاحية',
    passwordTooShort: 'يجب أن تتكون كلمة المرور من 8 أحرف على الأقل',
    passwordsDoNotMatch: 'كلمتا المرور غير متطابقتين',
    passwordResetFailed: 'فشل في إعادة تعيين كلمة المرور. يرجى المحاولة مرة أخرى.',
    passwordResetSuccess: 'تمت إعادة تعيين كلمة المرور بنجاح',
    resetPassword: 'إعادة تعيين كلمة المرور',
    confirmNewPassword: 'تأكيد كلمة المرور الجديدة',
    passwordMinLength: 'الحد الأدنى 8 أحرف',
    cancel: 'إلغاء',
    changing: 'جاري التغيير...',
    clearForm: 'مسح النموذج',
    resetChanges: 'إعادة تعيين التغييرات',
    clearFormConfirm: 'هل أنت متأكد من أنك تريد مسح جميع البيانات المدخلة؟ لا يمكن التراجع عن هذا الإجراء.',
    resetFormConfirm: 'هل أنت متأكد من أنك تريد إعادة تعيين جميع التغييرات؟ سيؤدي هذا إلى استعادة جميع الحقول إلى قيمها الأصلية.',
    searchAssignee: 'ابحث عن محرر...',
    searchClient: 'ابحث عن عميل...',
    noUsersFound: 'لم يتم العثور على مستخدمين',
    noClientsFound: 'لم يتم العثور على عملاء',

    // Time
    justNow: 'الآن',
    minutesAgo: 'دقيقة مضت',
    hoursAgo: 'ساعة مضت',
    daysAgo: 'يوم مضى',

    // Filters
    filter: 'تصفية',
    filters: 'الفلاتر',
    advancedFilters: 'الفلاتر المتقدمة',
    clearFilters: 'مسح الفلاتر',
    applyFilters: 'تطبيق الفلاتر',
    allStatus: 'جميع الحالات',
    allPriorities: 'جميع الأولويات',
    allTypes: 'جميع الأنواع',
    allAssignees: 'جميع المحررين',
    allDepartments: 'جميع الأقسام',
    allClients: 'جميع العملاء',
    allUsers: 'جميع المستخدمين',
    unassigned: 'غير معين',
    fromDate: 'من تاريخ',
    toDate: 'إلى تاريخ',
    dateFilters: 'تصفية حسب التواريخ',
    createdFrom: 'تاريخ الإنشاء من',
    createdTo: 'تاريخ الإنشاء إلى',
    dueDateFrom: 'تاريخ الاستحقاق من',
    dueDateTo: 'تاريخ الاستحقاق إلى',
    tags: 'الوسوم',
    enterTagsComma: 'أدخل الوسوم مفصولة بفواصل',
    withAttachments: 'مع مرفقات',
    withoutAttachments: 'بدون مرفقات',
    reset: 'إعادة تعيين',
    export: 'تصدير',
    exportToExcel: 'تصدير إلى Excel',
    exportToCSV: 'تصدير إلى CSV',
    createdBy: 'أنشئ بواسطة',
    department: 'القسم',
    dueDate: 'تاريخ الاستحقاق',
    priority: 'الأولوية',

    // Transaction Creation Form
    basicApplicationInformation: 'معلومات الطلب الأساسية',
    projectId: 'معرف المشروع',
    enterProjectId: 'أدخل معرف المشروع (اختياري)',
    enterApplicationTitle: 'أدخل عنوان الطلب',
    externalReferenceId: 'المرجع الخارجي',
    exampleReference: 'مثال: REF-2024-XXXX',
    description: 'الوصف',
    enterDescription: 'أدخل وصف تفصيلي للطلب',
    applicationType: 'نوع الطلب',
    selectType: 'اختر النوع',
    selectPriority: 'اختر الأولوية',
    selectStatus: 'اختر الحالة',
    clientEmail: 'البريد الإلكتروني للعميل',
    autoFilledClient: 'سيتم ملؤه تلقائياً عند اختيار العميل',
    autoFilledFromClient: 'تم ملؤه تلقائياً من بيانات العميل',
    clientPhone: 'رقم هاتف العميل',

    // Transaction Summary/Review
    reviewTransactionDetails: 'مراجعة تفاصيل المعاملة',
    applicationSummary: 'ملخص الطلب',

    // Login Page
    pleaseSignIn: 'يرجى تسجيل الدخول',
    emailAddress: 'البريد الإلكتروني',
    enterYourEmail: 'أدخل بريدك الإلكتروني',
    password: 'كلمة المرور',
    enterYourPassword: 'أدخل كلمة المرور',
    rememberMe: 'تذكرني',
    forgotPassword: 'نسيت كلمة المرور؟',
    signIn: 'تسجيل الدخول',
    dontHaveAccount: 'ليس لديك حساب؟',
    createAccount: 'إنشاء حساب',
    secureAccess: 'وصول آمن',
    realTimeTracking: 'تتبع في الوقت الفعلي',
    teamCollaboration: 'تعاون الفريق',
    // Login page (design v2)
    loginTitle: 'تسجيل الدخول',
    loginSubtitle: 'أدخل بياناتك للمتابعة',
    loginEmail: 'البريد الإلكتروني',
    loginForgot: 'نسيت كلمة المرور؟',
    loginSigningIn: 'جارٍ تسجيل الدخول…',
    loginShowPassword: 'إظهار كلمة المرور',
    loginHidePassword: 'إخفاء كلمة المرور',
    loginEmailInvalid: 'أدخل بريداً إلكترونياً صحيحاً',
    loginPasswordRequired: 'أدخل كلمة المرور',
    loginSystemName: 'نظام تتبع المعاملات',

    // Permission Errors
    permission_view_denied: 'ليس لديك صلاحية لعرض هذا الطلب',
    permission_edit_denied: 'ليس لديك صلاحية لتعديل هذا الطلب',
    permission_comment_denied: 'ليس لديك صلاحية للتعليق على هذا الطلب',
    permission_access_denied: 'ليس لديك صلاحية للوصول إلى هذا الطلب',
    permission_duplicate_denied: 'ليس لديك صلاحية لنسخ الطلبات',
    failed_to_load: 'فشل تحميل البيانات',

    // Documents
    myDocuments: 'المستندات',
    createDocument: 'إنشاء مستند',
    documentId: 'رقم المستند',
    title: 'العنوان',
    templateName: 'اسم القالب',
    mode: 'الوضع',
    completionPercentage: 'نسبة الإنجاز %',
    noDocumentsFound: 'لا توجد مستندات',
    searchDocuments: 'البحث عن مستندات...',
    allModes: 'جميع الأوضاع',
    loading: 'جاري التحميل...',

    // Document Create
    selectTemplate: 'اختر القالب',
    documentSetup: 'إعداد المستند',
    contentEditor: 'محرر المحتوى',
    back: 'رجوع',
    templateCategory: 'الفئة',
    sections: 'الأقسام',
    searchTemplates: 'البحث عن قوالب...',
    allCategories: 'جميع الفئات',
    selectTemplateFirst: 'الرجاء اختيار قالب للمتابعة',
    titleEn: 'العنوان (إنجليزي)',
    titleAr: 'العنوان (عربي)',
    clientProfile: 'ملف العميل',
    project: 'المشروع',
    transaction: 'المعاملة',
    creationMode: 'وضع الإنشاء',
    manual: 'يدوي',
    aiAssisted: 'بمساعدة الذكاء الاصطناعي',
    manualDescription: 'كتابة المحتوى يدوياً',
    aiDescription: 'توليد المحتوى بمساعدة الذكاء الاصطناعي',
    selectClient: 'اختر عميل...',
    selectProject: 'اختر مشروع...',
    selectTransaction: 'اختر معاملة...',

    // Content Editor
    progress: 'التقدم',
    sectionTitle: 'عنوان القسم',
    generateWithAI: 'توليد بالذكاء الاصطناعي',
    generating: 'جاري التوليد...',
    accept: 'قبول',
    regenerate: 'إعادة التوليد',
    markAsComplete: 'وضع علامة مكتمل',
    saveDraft: 'حفظ كمسودة',
    completeDocument: 'إكمال المستند',
    enterPrompt: 'أدخل التعليمات لتوليد الذكاء الاصطناعي...',
    generatedContent: 'المحتوى المولد',
    autoSaving: 'حفظ تلقائي...',

    // Landing Page v3 (Greptile style)
    nav_product: 'المنتج', nav_features: 'المميزات', nav_pricing: 'الأسعار', nav_login: 'تسجيل الدخول', nav_start: 'ابدأ الآن',
    hero_badge: 'منصة مدعومة بالذكاء الاصطناعي', hero_title_line1: 'ابدأ أعمالك', hero_title_line2: 'بذكاء',
    hero_tagline: 'منصة واحدة لإدارة مشاريعك ومعاملاتك\nواجتماعاتك ومستنداتك\nبالذكاء الاصطناعي',
    reasons_title: 'لماذا Nuagent',
    reasons_why: 'لماذا',
    reason_1: 'أنشئ عرضك المالي والفني لعملائك في 3 دقائق',
    reason_2: 'جهّز محضر اجتماعك وتتبع الموافقات بسهولة',
    reason_3: 'استخدم القوالب الذكية لإنشاء عقودك ومراسلاتك الرسمية',
    reason_4: 'تابع حالة مشاريعك ومعاملاتك في لوحة واحدة',
    hero_cta_start: 'ابدأ مجاناً', hero_cta_sub: 'لا حاجة لبطاقة ائتمان • تجربة مجانية 14 يوماً', hero_mockup: '[ عرض المنتج ]',
    trust_label: 'موثوقة من أكثر من 500 شركة لإدارة عملياتها اليومية', trust_link: 'اكتشف لماذا تحب الشركات نيوإيجنت',
    trust_secure: 'خاص وآمن', trust_anywhere: 'الوصول من أي مكان', trust_support: 'دعم على مدار الساعة',
    trust_uptime: 'متصل دائماً', trust_mobile: 'متوافق مع الجوال', trust_bilingual: 'ثنائي اللغة عربي/إنجليزي',
    feat_badge: 'الوحدات الأساسية', feat_title: 'ساعدك الأيمن في العمل',
    feat_transactions_badge: 'المعاملات', feat_transactions_title: 'تتبع كل معاملة من البداية للنهاية',
    feat_transactions_desc: 'معرّفات تلقائية، رموز QR، توزيع ذكي، عمليات جماعية، وتحديثات فورية.',
    feat_transactions_visual: '[ متتبع المعاملات ]',
    feat_projects_badge: 'المشاريع', feat_projects_title: 'مشاريع تتقدم فعلاً',
    feat_projects_desc: 'مراحل إنجاز، أنشطة، تتبع التقدم، تعاون جماعي. لوحات بيانية تبقي الجميع على اطلاع.',
    feat_projects_visual: '[ لوحة المشاريع ]',
    feat_proposals_badge: 'العروض', feat_proposals_title: 'عروض احترافية في دقائق',
    feat_proposals_desc: 'قوالب بالذكاء الاصطناعي، سحب وإفلات، جداول دفع، وتصدير PDF بنقرة واحدة.',
    feat_proposals_visual: '[ منشئ العروض ]',
    feat_meetings_badge: 'الاجتماعات', feat_meetings_title: 'محاضر اجتماعات تكتب نفسها',
    feat_meetings_desc: 'تتبع الحضور وبنود العمل والقرارات والموافقات بسير عمل رقمي متعدد المستويات.',
    feat_meetings_visual: '[ متتبع المحاضر ]',
    ai_badge: 'مدعوم بالذكاء الاصطناعي', ai_title: 'ذكاء مدمج في كل سير عمل.',
    ai_proposal_title: 'العروض الفنية والمالية', ai_proposal_desc: 'ارفع أي قالب تصميم وسيقوم محرك الذكاء الاصطناعي باستخراج التخطيط وتعيين المحتوى وإنشاء عروض مطابقة تلقائياً.',
    ai_docs_title: 'المستندات والعقود', ai_docs_desc: 'انشئ المستندات والعقود والخطابات بتنسيق وقوالب ثابتة بالذكاء الاصطناعي',
    ai_assign_title: 'الأتمتة الذكية', ai_assign_desc: 'أنشئ المقترح، وقّع العقد، ادر مدفوعاتك وتابع المشروع سير عملٍ ذكي ومؤتمت من أول خطوة حتى التسليم.',
    security_badge: 'الأمان', security_title: 'تصميم يضع الأمان أولاً',
    security_onprem_title: 'استضافة داخلية (On-Premise)', security_onprem_desc: 'استضافة داخلية أو محلية لضمان آمن للبيانات والتوافق مع متطلبات الأمن السيبراني',
    security_enterprise_title: 'أمان على مستوى المؤسسات', security_enterprise_desc: 'صلاحيات محكمة، سجلات تدقيق، بيانات مشفرة، وامتثال جاهز للائحة حماية البيانات.',
    testimonials_badge: 'آراء العملاء', testimonials_title: 'من الشركات التي تستخدم نيوإيجنت',
    testimonials_title_prefix: 'من الشركات التي تستخدم',
    testimonial_1_quote: 'نيوإيجنت غيّر طريقة إدارتنا للمشاريع في MDC. أصبح فريقنا أكثر إنتاجية، والقرارات تُتخذ بناءً على بيانات لحظية.',
    testimonial_1_name: 'م. محمد الدوسري', testimonial_1_role: 'الرئيس التنفيذي، MDC',
    testimonial_2_quote: 'محاضر الاجتماعات أصبحت تُكتب وتُوثّق وتُرسل للعملاء تلقائياً. ما كان يأخذ منا ساعات من التفريغ والمراجعة، أصبح جاهزاً بضغطة زر.',
    testimonial_2_name: 'محمد تيرو', testimonial_2_role: 'مدير العمليات، MDC',
    testimonial_3_quote: 'أصبحنا نُعدّ العرض الفني والمالي بدقة عالية في أقل من 3 دقائق. وفّرنا وقتاً ثميناً، وزادت فرصنا في الفوز بالمشاريع.',
    testimonial_3_name: 'محمد السبيعي', testimonial_3_role: 'مدير تطوير الأعمال، MDC',
    pricing_badge: 'الأسعار', pricing_title: 'أسعار بسيطة لأي عمل.', pricing_subtitle: 'اختر الباقة المناسبة لفريقك. جميع الباقات تشمل الدعم ثنائي اللغة.',
    plan_starter_name: 'أساسي', plan_starter_users: '', plan_starter_price: '25 ريال', plan_starter_period: '/مستخدم/شهرياً',
    plan_starter_desc: 'مثالية للفرق الصغيرة', plan_starter_cta: 'اختر الباقة',
    plan_starter_f1: 'جميع الوحدات الأساسية', plan_starter_f2: '', plan_starter_f3: '10 جيجابايت تخزين',
    plan_starter_f4: 'دعم عبر البريد', plan_starter_f5: 'استضافة سحابية', plan_starter_f6: 'واجهة ثنائية اللغة',
    plan_business_name: 'إحترافي', plan_business_users: '', plan_business_popular: 'الأكثر اختياراً',
    plan_business_price: '75 ريال', plan_business_period: '/مستخدم/شهرياً', plan_business_desc: 'مثالية للشركات المتوسطة',
    plan_business_cta: 'اختر الباقة',
    plan_business_f1: 'كل ما في الأساسي', plan_business_f2: '', plan_business_f3: '100 جيجابايت تخزين',
    plan_business_f4: 'دعم ذو أولوية', plan_business_f5: 'منشئ العروض بالذكاء الاصطناعي', plan_business_f6: 'تحليلات متقدمة',
    plan_business_f7: 'هوية بصرية مخصصة', plan_business_f8: 'وصول API',
    plan_enterprise_name: 'المؤسسات و الشركات', plan_enterprise_users: '', plan_enterprise_price: 'تواصل معنا',
    plan_enterprise_desc: 'مصممة للمؤسسات الكبيرة', plan_enterprise_cta: 'تواصل مع المبيعات',
    plan_enterprise_f1: 'كل ما في الإحترافي', plan_enterprise_f2: 'مستخدمين غير محدودين', plan_enterprise_f3: 'تخزين غير محدود',
    plan_enterprise_f4: 'خيار النشر الداخلي', plan_enterprise_f5: 'مدير حساب مخصص', plan_enterprise_f6: 'تكاملات مخصصة',
    plan_enterprise_f7: 'ضمان اتفاقية مستوى الخدمة', plan_enterprise_f8: 'تدريب وتأهيل',
    faq_badge: 'الأسئلة الشائعة', faq_title: 'أسئلة متكررة',
    faq_q1: 'ماذا تشمل التجربة المجانية؟', faq_a1: 'وصول كامل لمدة 14 يوماً لجميع مميزات خطة الأعمال. لا حاجة لبطاقة ائتمان. إلغاء في أي وقت.',
    faq_q2: 'هل يمكنني التبديل بين السحابي والداخلي؟', faq_a2: 'نعم، ندعم الترحيل السلس بين النشر السحابي والداخلي في أي وقت.',
    faq_q3: 'هل تدعمون العربية والإنجليزية؟', faq_a3: 'نعم، دعم أصلي كامل ثنائي اللغة مع تخطيط من اليمين لليسار. ليس طبقة ترجمة بل مبني من الأساس.',
    faq_q4: 'هل بياناتي آمنة؟', faq_a4: 'تشفير 256-بت، صلاحيات محكمة، سجلات تدقيق شاملة، وامتثال جاهز للائحة حماية البيانات.',
    faq_q5: 'ما القطاعات التي تخدمونها؟', faq_a5: 'البناء، القانون، المالية، الهندسة، الاستشارات، العقارات، الحكومة، الرعاية الصحية، والتعليم.',
    faq_q6: 'كيف أبدأ؟', faq_a6: 'سجّل مجاناً على nuagent.net. لا حاجة لبطاقة ائتمان. مساحة عملك جاهزة في أقل من دقيقتين.',
    faq_contact: 'لا زال لديك أسئلة؟', faq_more: 'تواصل معنا',
    cta_title: 'مستعد لتبسيط عملياتك؟', cta_subtitle: 'ابدأ تجربتك المجانية لمدة 14 يوماً اليوم.',
    cta_button: 'ابدأ مجاناً', cta_no_card: 'لا حاجة لبطاقة ائتمان',
    footer_tagline: 'جعل إدارة الأعمال بسيطة وذكية وآمنة.',
    footer_product: 'المنتج', footer_company: 'الشركة', footer_legal: 'قانوني',
    footer_contact: 'تواصل معنا', footer_about: 'عن الشركة', footer_careers: 'وظائف', footer_blog: 'المدونة',
    footer_privacy: 'سياسة الخصوصية', footer_terms: 'شروط الخدمة', footer_sla: 'اتفاقية مستوى الخدمة',
    footer_security: 'الأمان', footer_api_docs: 'توثيق API',
    footer_rights: 'جميع الحقوق محفوظة.', footer_powered: 'بدعم من نواس',
    // Contact Page
    contact_hero_title: 'تواصل مع المبيعات.', contact_hero_subtitle: 'شكراً لاهتمامك بنيوإيجنت. املأ النموذج وسيتواصل معك فريقنا في أقرب وقت.',
    contact_hero_subtitle_prefix: 'شكراً لاهتمامك بـ',
    contact_hero_subtitle_suffix: '. املأ النموذج وسيتواصل معك فريقنا في أقرب وقت.',
    contact_first_name: 'الاسم الأول', contact_last_name: 'اسم العائلة',
    contact_first_name_ph: 'أحمد', contact_last_name_ph: 'محمد',
    contact_email: 'البريد الإلكتروني', contact_company: 'اسم الشركة',
    contact_email_ph: 'ahmed@company.com', contact_company_ph: 'شركة المثال',
    contact_team_size: 'كم عدد الموظفين؟', contact_team_select: 'اختر حجم الفريق',
    contact_message: 'ما الذي يساعدنا في معرفته؟',
    contact_message_ph: 'أخبرنا عن أعمالك وما الذي تتطلع لتحقيقه...',
    contact_submit: 'تواصل معنا',
    contact_success_title: 'تم الإرسال!', contact_success_desc: 'شكراً لتواصلك. سيرد عليك فريقنا خلال 24 ساعة.',
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    return localStorage.getItem('language') || 'ar';
  });

  const isRTL = language === 'ar';

  const setLanguage = (lang: string) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
    // Removed window.location.reload() - React state handles language changes
    // This preserves user permissions and avoids race conditions during auth refresh
  };

  /** The admin's DEFAULT_LANGUAGE, for a visitor who has never chosen one.
   *  Not persisted, so a later change by the admin still reaches them. */
  const applyDefaultLanguage = useCallback((lang: string) => {
    if (localStorage.getItem('language') || (lang !== 'ar' && lang !== 'en')) return;
    setLanguageState(lang);
  }, []);

  const t = (key: string, fallback?: string): string => {
    return translations[language]?.[key] || translations['en']?.[key] || fallback || key;
  };

  useEffect(() => {
    // Set initial direction
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language, isRTL]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, applyDefaultLanguage, isRTL, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};